import { useQuery } from "@tanstack/react-query";
import { clinicSettingsApi, clinicSettingsQueryKeys } from "../api/clinic";
import type { ClinicSettings } from "../api/clinic";
import { formatPhone } from "../format";

/**
 * The clinic block printed at the top of a document handed to a patient (#19168, L2).
 *
 * <p>Every field comes from Cài đặt → Phòng khám of the tenant signed in. The receipt and the visit
 * summary used to print a hard-coded sample clinic — name, address, phone and a tax code that
 * belong to nobody — on paper that leaves the building, which is a legal problem, not a cosmetic
 * one. There is deliberately no fallback value: a field the clinic has not filled is simply not
 * printed, label included.
 */
export interface ClinicHeader {
  name: string;
  address: string | null;
  /** "ĐT: … · MST: …", with only the parts the clinic has filled; null when neither is. */
  phoneAndTax: string | null;
  email: string | null;
}

function filled(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t ? t : null;
}

export function toClinicHeader(s: Pick<
  ClinicSettings,
  "name" | "address" | "phone" | "email" | "taxCode"
>): ClinicHeader {
  const phone = filled(s.phone);
  const tax = filled(s.taxCode);
  const parts = [phone ? `ĐT: ${formatPhone(phone)}` : null, tax ? `MST: ${tax}` : null].filter(
    (p): p is string => p !== null,
  );
  return {
    name: filled(s.name) ?? "",
    address: filled(s.address),
    phoneAndTax: parts.length > 0 ? parts.join(" · ") : null,
    email: filled(s.email),
  };
}

/**
 * Reads the header from {@code GET /v1/clinic/settings} ({@code clinic.read}). Every role that can
 * open a receipt or a visit summary — clinic_owner, doctor, receptionist — holds that permission
 * (checked 29/09/2026 across all tenants), so no read grant had to be widened for this.
 */
export function useClinicHeader() {
  const q = useQuery({
    queryKey: clinicSettingsQueryKeys.current(),
    queryFn: () => clinicSettingsApi.get(),
    staleTime: 5 * 60_000,
  });
  return {
    header: q.data ? toClinicHeader(q.data) : null,
    /** Settled either way — an auto-print must not fire before this, or the sheet has no header. */
    settled: !q.isLoading,
    isError: q.isError,
  };
}
